import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Cookies from 'js-cookie';
import LandingPage from './LandingPage_page.tsx';
import SignupPage from './SignupPage_page.tsx';
import UserPage from './user_page.tsx';
import GoogleSignInPage from './google.tsx';
import SignInSuccessPage from './SignInSuccessPage.tsx';
import BlogList from './BlogList.tsx';
import BlogPost from './BlogPost.tsx';
// import Team from './team.tsx';
import ResearchProductPage from './ResearchProductPage.tsx';
import SouldribuRoboticsPage from './SouldribuRobotics_page.tsx';
import ComputerBrainInterface from './ComputerBrainInterface.tsx';
import TransformerEngine from './TransformerEngine.tsx';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Create a Theme Context
export const ThemeContext = React.createContext({
  theme: 'light',
  toggleTheme: () => {},
});

const AppContent: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [theme, setTheme] = useState<string>('light');
  const navigate = useNavigate();

  // Initialize theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('app-theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Toggle theme function
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('app-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      console.log('Checking authentication:', !!token);
      setIsAuthenticated(!!token);
    };
    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  const handleLogout = () => {
    console.log('Logging out');
    localStorage.removeItem('access_token');
    setIsAuthenticated(false);
    navigate('/');
  };

  const handleAuthentication = (token: string, isGoogleSignIn: boolean = false) => {
    console.log('Handling authentication');
    localStorage.setItem('access_token', token);
    setIsAuthenticated(true);
    console.log('isAuthenticated set to true');
    if (isGoogleSignIn) {
      navigate('/signin-success');
    } else {
      navigate('/dashboard');
    }
  };

  console.log('Current isAuthenticated state:', isAuthenticated);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LandingPage />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              <UserPage onLogout={handleLogout} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/signup"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <SignupPage setIsAuthenticated={(token) => handleAuthentication(token, false)} />
            )
          }
        />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/title/:title" element={<BlogPost />} />
        <Route
          path="/singing_google"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <GoogleSignInPage onAuthentication={(token) => handleAuthentication(token, true)} />
            )
          }
        />
        <Route
          path="/signin-success"
          element={
            isAuthenticated ? (
              <SignInSuccessPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* <Route path="/Team" element={<Team />} />  */}
        <Route path="/research" element={<ResearchProductPage />} />
        <Route path="/robotics" element={<SouldribuRoboticsPage />} />
        <Route path="/computer-brain-interface" element={<ComputerBrainInterface />} />
        <Route path="/transformer-engine" element={<TransformerEngine />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeContext.Provider>
  );
};

const App: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <AppContent />
      </Router>
    </GoogleOAuthProvider>
  );
};

export default App;